import React, { useState, useEffect, useRef } from "react";
import OrderList from "./OrderList";
import { useLocation } from "react-router-dom";
import { SearchOutlined, CalendarOutlined } from "@ant-design/icons";

const AdminOrder = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [localSearchValue, setLocalSearchValue] = useState(""); // Local input value
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFilter, setDateFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const debounceTimer = useRef(null);

  // Lấy các tham số từ URL khi component mount
  useEffect(() => {
    const search = params.get("search") || "";
    const sort = params.get("sortBy") || "date";
    const order = params.get("sortOrder") || "desc";
    const dateFilterParam = params.get("dateFilter") || "";
    const fromDate = params.get("dateFrom") || "";
    const toDate = params.get("dateTo") || "";
    const page = parseInt(params.get("page")) || 1;

    setSearchKeyword(search);
    setLocalSearchValue(search);
    setSortBy(sort);
    setSortOrder(order);
    setDateFilter(dateFilterParam);
    setDateFrom(fromDate);
    setDateTo(toDate);
    setCurrentPage(page);
  }, [location.search]);

  // Debounce search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (localSearchValue !== searchKeyword) {
        setSearchKeyword(localSearchValue);
        setCurrentPage(1);
        updateURL(
          localSearchValue,
          sortBy,
          sortOrder,
          dateFilter,
          dateFrom,
          dateTo,
          1
        );
      }
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearchValue, sortBy, sortOrder, dateFilter, dateFrom, dateTo]);

  const handleSearchKeywordChange = (event) => {
    const value = event.target.value;
    setLocalSearchValue(value);
  };

  const handleSortByChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    updateURL(
      searchKeyword,
      newSortBy,
      sortOrder,
      dateFilter,
      dateFrom,
      dateTo,
      1
    );
  };

  const handleSortOrderChange = (newOrder) => {
    setSortOrder(newOrder);
    setCurrentPage(1);
    updateURL(searchKeyword, sortBy, newOrder, dateFilter, dateFrom, dateTo, 1);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    // Clear custom date range when using predefined filters
    if (filter) {
      setDateFrom("");
      setDateTo("");
      updateURL(searchKeyword, sortBy, sortOrder, filter, "", "", 1);
    } else {
      updateURL(searchKeyword, sortBy, sortOrder, "", dateFrom, dateTo, 1);
    }
    setCurrentPage(1);
  };

  const handleDateFromChange = (event) => {
    const date = event.target.value;
    setDateFrom(date);
    // Clear predefined filter when using custom dates
    setDateFilter("");
    setCurrentPage(1);
    updateURL(searchKeyword, sortBy, sortOrder, "", date, dateTo, 1);
  };

  const handleDateToChange = (event) => {
    const date = event.target.value;
    setDateTo(date);
    // Clear predefined filter when using custom dates
    setDateFilter("");
    setCurrentPage(1);
    updateURL(searchKeyword, sortBy, sortOrder, "", dateFrom, date, 1);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchKeyword(localSearchValue);
    setCurrentPage(1);
    updateURL(
      localSearchValue,
      sortBy,
      sortOrder,
      dateFilter,
      dateFrom,
      dateTo,
      1
    );
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateURL(
      searchKeyword,
      sortBy,
      sortOrder,
      dateFilter,
      dateFrom,
      dateTo,
      newPage
    );
  };

  const updateURL = (
    search,
    sortByParam,
    sortOrderParam,
    dateFilterParam,
    dateFromParam,
    dateToParam,
    newPage = 1
  ) => {
    const params = new URLSearchParams();
    params.set("page", newPage.toString());

    if (search.trim()) params.set("search", search.trim());
    if (sortByParam !== "date") params.set("sortBy", sortByParam);
    if (sortOrderParam !== "desc") params.set("sortOrder", sortOrderParam);
    if (dateFilterParam) params.set("dateFilter", dateFilterParam);
    if (dateFromParam) params.set("dateFrom", dateFromParam);
    if (dateToParam) params.set("dateTo", dateToParam);

    window.history.replaceState({}, "", `/admin/orders?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setLocalSearchValue("");
    setSortBy("date");
    setSortOrder("desc");
    setDateFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    window.history.replaceState({}, "", "/admin/orders?page=1");
  };

  const sortOptions = [
    { value: "date", label: "Ngày tạo" },
    { value: "total", label: "Tổng tiền" },
    { value: "customer", label: "Tên khách hàng" },
  ];

  const dateFilterOptions = [
    { value: "", label: "Tất cả" },
    { value: "today", label: "Hôm nay" },
    { value: "yesterday", label: "Hôm qua" },
    { value: "thisWeek", label: "Tuần này" },
    { value: "last7Days", label: "7 ngày qua" },
    { value: "thisMonth", label: "Tháng này" },
    { value: "lastMonth", label: "Tháng trước" },
    { value: "last30Days", label: "30 ngày qua" },
  ];

  return (
    <div className="bg-gray-50 h-screen overflow-y-auto">
      <div className="min-h-screen overflow-y-auto">
        <div className="p-1 space-y-2">
          {/* Header + Filters Combined */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Quản lý đơn hàng
                </h1>
                <p className="text-gray-600 mt-1">
                  Quản lý và theo dõi trạng thái đơn hàng trong hệ thống
                </p>
              </div>
            </div>

            {/* Main Filters */}
            <div className="border-t border-gray-200 pt-4">
              <form onSubmit={handleSearchSubmit} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Search Input */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <SearchOutlined className="mr-1" />
                      Tìm kiếm
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Mã đơn, tên khách hàng, SĐT..."
                        value={localSearchValue}
                        onChange={handleSearchKeywordChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                      />

                      {localSearchValue !== searchKeyword && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sort Options */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sắp xếp theo
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={sortBy}
                        onChange={(e) => handleSortByChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={sortOrder}
                        onChange={(e) => handleSortOrderChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="desc">Giảm dần</option>
                        <option value="asc">Tăng dần</option>
                      </select>
                    </div>
                  </div>

                  {/* Date Filter */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CalendarOutlined className="mr-1" />
                      Lọc theo thời gian
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    >
                      {dateFilterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Custom Date Range */}
                {!dateFilter && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Từ ngày
                      </label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={handleDateFromChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Đến ngày
                      </label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={handleDateToChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                  >
                    <SearchOutlined />
                    Tìm kiếm
                  </button>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                  >
                    Xóa bộ lọc
                  </button>
                </div>
              </form>

              {/* Active Filters Display */}
              {(searchKeyword ||
                sortBy !== "date" ||
                sortOrder !== "desc" ||
                dateFilter ||
                dateFrom ||
                dateTo) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      Bộ lọc hiện tại:
                    </span>

                    {searchKeyword && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Tìm kiếm: "{searchKeyword}"
                      </span>
                    )}

                    {sortBy !== "date" && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Sắp xếp:{" "}
                        {sortOptions.find((opt) => opt.value === sortBy)?.label}
                      </span>
                    )}

                    {sortOrder !== "desc" && (
                      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Thứ tự: Tăng dần
                      </span>
                    )}

                    {dateFilter && (
                      <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Thời gian:{" "}
                        {
                          dateFilterOptions.find(
                            (opt) => opt.value === dateFilter
                          )?.label
                        }
                      </span>
                    )}

                    {(dateFrom || dateTo) && (
                      <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Khoảng: {dateFrom || "..."} → {dateTo || "..."}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <OrderList
              searchKeyword={searchKeyword}
              sortBy={sortBy}
              sortOrder={sortOrder}
              dateFilter={dateFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrder;
